import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useMutation } from "@apollo/client";
import { ReportLock, ReportUnLock } from "../../apollo-client/api-query";
import { useDispatch } from "react-redux";
import { openModal } from "../../store";

const STATUS = [
  {
    title: 'Unlocked',
    value: false,
  },
  {
    title: 'Locked',
    value: true 
  }
]

export default function LockStatusSwitch({ row, reportId, lockStatus = false, refetchReports, isReadonly = false }) {
  const dispatch = useDispatch()
  const [lockReport, { loading: lockingReport }] = useMutation(ReportLock, {fetchPolicy: 'network-only'})
  const [unlockReport, { loading: unlockingReport }] = useMutation(ReportUnLock, {fetchPolicy: 'network-only'})
  const isMutating = lockingReport || unlockingReport

  const [selected, setSelected] = useState(STATUS[0].value)

  useMemo(() => setSelected(!lockStatus), [lockStatus])

  const handleChange = async () => {
    if (isReadonly || isMutating) {
      return false
    }
    if (!row?.needsReview) {
      const variables = {
        variables: {
          reportId: reportId
        }
      }

      try {
        if (selected) {
          await lockReport(variables)
          refetchReports()
        } else {
          if (row.name === 'Brain & Memory') {
            dispatch(
              openModal({
                currentModal: 'BRAIN_MEMORY_CONSENT_MODAL',
                modalProps: {
                  onConfirm: async () => {
                    try {
                      await unlockReport(variables)
                      refetchReports()
                    } catch (e) {
                      console.error('[LockStatusSwitch] unlock failed:', e?.message)
                      refetchReports()
                    }
                  }
                }
            }))
          } else {
            await unlockReport(variables)
            refetchReports()
          }
        }
      } catch (e) {
        // Race condition: the report's lock state changed between the UI render
        // and the mutation hitting the backend (e.g. "Report is not locked" when
        // trying to unlock something that was already unlocked by a rapid click).
        // Refetch to sync the UI with the server's true state.
        console.error('[LockStatusSwitch] mutation failed:', e?.message)
        refetchReports()
      }
    }
  }

  return (
    <Box 
      sx={{
        bgcolor: selected ? 'primary.mediumGreen' : 'primary.mediumGray',
        border: '0px solid',
        borderRadius: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isReadonly ? 'center' : 'space-between',
        cursor: isReadonly ? 'default' : isMutating ? 'wait' : 'pointer',
        opacity: isMutating ? 0.6 : 1,
        height: '31px',
        width: !selected ? '96px' : '114px',
        padding: selected ? '4px 4px 4px 8px' : '4px 8px 4px 4px',
      }}
      onClick={handleChange}
    >
      {STATUS.map(option => (
        <Box
          key={option.title}
          sx={isReadonly ? {}: {
            minWidth: selected === option.value ? '22px' : 'auto',
            height: selected === option.value ? '22px' : '100%',
            borderRadius: selected === option.value ? '50%' : '0px',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: selected === option.value ? 'white' : 'transparent',
          }}
        >
          <Typography
            key={option.value}
            variant="h4"
            sx={{
              color: 'primary.white',
              whiteSpace: 'nowrap',
              display: selected === option.value ? 'none' : 'block',
              mr: option.title === 'Unlocked' ? '4px' : 0
            }}
          >
            {option.title}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}